import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Plus, CalendarIcon, Users, Filter, Trash2, Phone, MessageCircle, Download } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/utils/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTER_STORAGE_KEY = "gosmart.leads.filters";
const EMPTY_FILTERS = { search: "", rm: "all", status: "all", addedDate: "", followupDate: "", shiftingDate: "", sortBy: "created_at" };

const savedLeadFilters = () => {
  try {
    return { ...EMPTY_FILTERS, ...JSON.parse(sessionStorage.getItem(FILTER_STORAGE_KEY) || "{}") };
  } catch {
    return { ...EMPTY_FILTERS };
  }
};

const INPUT_CLS =
  "rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm text-[#0F172A] placeholder:text-[#94A3B8]";
const lbl = "text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1.5";
const IC  = INPUT_CLS;

const EMPTY_FORM = {
  customer_name: "",
  phone: "",
  alternate_number: "",
  lead_source: "",
  moving_from: "",
  moving_to: "",
  from_location: "",
  to_location: "",
  load_size: "",
  quotation: "",
  additional_remarks: "",
  status: "",
};

const formatRoute = (start, end) => {
  if (start && end) return `${start} → ${end}`;
  return start || end || "—";
};

const clipText = (value, maxLength = 50) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}....` : value;

const leadAddedDateTime = value => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : format(date, "dd MMM yyyy, hh:mm a");
};

const formatQuotedAmount = value => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const phoneDigits = value => String(value || "").replace(/\D/g, "");
const whatsappNumber = value => {
  const digits = phoneDigits(value);
  return digits.length === 10 ? `91${digits}` : digits;
};

const shiftingWithinThreeDays = value => {
  if (!value) return false;
  const shifting = new Date(`${value}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysAway = Math.ceil((shifting.getTime() - today.getTime()) / 86400000);
  return daysAway >= 0 && daysAway <= 3;
};

const ClippedText = ({ value, className = "" }) => {
  const isClipped = value.length > 50;
  return (
    <span className={`relative inline-block group ${className}`}>
      {clipText(value)}
      {isClipped && (
        <span
          role="tooltip"
          className="pointer-events-none absolute left-0 bottom-full z-50 mb-1 hidden w-max max-w-sm whitespace-normal bg-[#0F172A] px-2.5 py-1.5 text-[11px] font-normal leading-4 text-white shadow-lg group-hover:block"
        >
          {value}
        </span>
      )}
    </span>
  );
};

const FormField = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">
      {label}
    </label>
    {children}
  </div>
);

const LEAD_STATUSES = ["Open", "Contacted", "Qualified", "Closed Won", "Closed Lost", "Invalid"];
const STATUS_FILTER_OPTIONS = [
  { value: "Open", label: "Open" },
  { value: "Contacted", label: "Contacted" },
  { value: "Qualified", label: "Qualified" },
  { value: "confirmed", label: "Confirmed" },
  { value: "declined", label: "Declined" },
  { value: "Invalid", label: "Invalid" },
];
const VIEW_STATUS_OPTIONS = {
  active: STATUS_FILTER_OPTIONS.filter(option => ["Open", "Contacted", "Qualified"].includes(option.value)),
  confirmed: STATUS_FILTER_OPTIONS.filter(option => option.value === "confirmed"),
  closed: STATUS_FILTER_OPTIONS.filter(option => ["declined", "Invalid"].includes(option.value)),
};

const STATUS_CFG = {
  Open:        "bg-red-50 text-red-700 border-red-200",
  New:         "bg-red-50 text-red-700 border-red-200",   // legacy
  Contacted:   "bg-green-50 text-green-700 border-green-200",
  Active:      "bg-cyan-50 text-cyan-700 border-cyan-200",
  Qualified:   "bg-green-50 text-green-700 border-green-200",
  Confirmed:   "bg-orange-50 text-orange-700 border-orange-200",
  "Closed Won":"bg-orange-50 text-orange-700 border-orange-200",
  Converted:   "bg-orange-50 text-orange-700 border-orange-200", // legacy
  Declined:    "bg-red-50 text-red-700 border-red-200",
  "Closed Lost":"bg-red-50 text-red-700 border-red-200",
  Closed:      "bg-red-50 text-red-700 border-red-200",  // legacy
  Invalid:     "bg-red-50 text-red-700 border-red-200",
};

const STATUS_DISPLAY = {
  "Closed Won": "Confirmed",
  Converted: "Confirmed",
  "Closed Lost": "Declined",
  Closed: "Declined",
  Active: "Follow Up/Active",
  New: "Open",
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${
    STATUS_CFG[status] || "bg-gray-100 text-gray-500 border-gray-200"
  }`}>
    {STATUS_DISPLAY[status] || status}
  </span>
);

/* ── LeadsSection sub-component ─────────────────────────────────────────── */
function LeadsSection({ title, subtitle, leads, navigate, canDelete, onRequestDelete, onDownloadQuotation }) {
  if (leads.length === 0 && title) {
    return (
      <div className="border-b border-[#E2E8F0] last:border-b-0">
        <div className="px-4 py-3 bg-[#F9FAFB] border-b border-[#E2E8F0] flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#475569]">{title}</span>
          <span className="text-[10px] text-[#94A3B8]">— none</span>
        </div>
      </div>
    );
  }
  return (
    <div className="border-b border-[#E2E8F0] last:border-b-0">
      {title && (
        <div className="px-4 py-3 bg-[#F9FAFB] border-b border-[#E2E8F0] flex items-center gap-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#475569]">{title}</span>
          {subtitle && <span className="text-[10px] text-[#94A3B8]">{subtitle}</span>}
        </div>
      )}
      <table className="w-full min-w-[1360px] text-sm" data-testid={title ? `leads-table-${title.replace(/\s+/g,"-").toLowerCase()}` : "leads-table"}>
        <thead>
          <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
            <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]">Name</th>
            <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]">RM</th>
            <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]">Route</th>
            <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]"><span className="block">Added Date</span><span className="block">and Time</span></th>
            <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]"><span className="block">Assigned Date</span><span className="block">and Time</span></th>
            <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]">Next Follow Up Date</th>
            <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]">Quoted Amount</th>
            <th className="text-center px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]">Quotation</th>
            <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]">Status</th>
            <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]">Shifting Date</th>
            {canDelete && <th className="text-center px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569]">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {leads.map((lead, idx) => {
            const rowBg = idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]";
            const route = formatRoute(lead._startingCity, lead._endingCity);
            return (
                <tr
                  key={lead.id}
                  data-testid={`lead-row-${idx}`}
                  tabIndex={0}
                  role="link"
                  onClick={() => navigate(`/leads/${lead.id}`)}
                  onKeyDown={event => { if (event.key === "Enter" || event.key === " ") navigate(`/leads/${lead.id}`); }}
                  className={`border-b border-[#E2E8F0] cursor-pointer transition-colors hover:bg-[#EEF2FF] focus:bg-[#EEF2FF] focus:outline-none ${rowBg}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap" data-testid={`lead-name-${idx}`}>
                    <p className="font-semibold text-[#1B2A6B]">{lead.customer_name}</p>
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className="text-xs font-bold text-[#334155]">{lead.phone || "—"}</span>
                      {phoneDigits(lead.phone) && <>
                        <a
                          href={`tel:${phoneDigits(lead.phone)}`}
                          onClick={event => event.stopPropagation()}
                          aria-label={`Call ${lead.customer_name}`}
                          title="Call"
                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 text-blue-700 hover:bg-blue-700 hover:text-white"
                        ><Phone size={12} /></a>
                        <a
                          href={`https://wa.me/${whatsappNumber(lead.phone)}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={event => event.stopPropagation()}
                          aria-label={`WhatsApp ${lead.customer_name}`}
                          title="WhatsApp"
                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-50 text-green-700 hover:bg-green-700 hover:text-white"
                        ><MessageCircle size={12} /></a>
                      </>}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {lead._assignedRMName ? (
                      <span className="font-semibold text-[#1B2A6B]">{lead._assignedRMName}</span>
                    ) : (
                      <span className="text-[#94A3B8]">Unassigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap max-w-[260px]">
                    <ClippedText value={route} className="font-medium text-[#0F172A]" />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-[#475569]">
                    {leadAddedDateTime(lead.created_at)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-[#475569]">
                    {leadAddedDateTime(lead.assigned_at)}
                  </td>
                  <td className="px-4 py-3 min-w-[170px]">
                    {lead.next_followup_date ? (
                      <div>
                        <p className="text-xs font-medium text-[#0F172A]">{format(new Date(`${lead.next_followup_date}T00:00:00`), "dd MMM yyyy")}</p>
                        <p className="mt-0.5 max-w-[190px] truncate text-[10px] text-[#64748B]" title={lead.next_followup_action || ""}>{lead.next_followup_action || "—"}</p>
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs font-semibold text-[#0F172A]">{formatQuotedAmount(lead.quotation)}</td>
                  <td className="px-4 py-3 text-center">
                    {lead.quotation_file_path ? (
                      <button
                        type="button"
                        aria-label={`Download quotation for ${lead.customer_name}`}
                        title={lead.quotation_file_name || "Download quotation"}
                        onClick={event => { event.stopPropagation(); onDownloadQuotation(lead); }}
                        className="inline-flex h-8 w-8 items-center justify-center border border-blue-200 text-[#1B2A6B] hover:bg-blue-50"
                      >
                        <Download size={14} />
                      </button>
                    ) : <span className="text-[#CBD5E1]">—</span>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={lead.status} /></td>
                  <td className={`px-4 py-3 whitespace-nowrap text-xs ${shiftingWithinThreeDays(lead.shifting_date) ? "font-bold text-red-600" : "text-[#475569]"}`}>
                    {lead.shifting_date ? format(new Date(`${lead.shifting_date}T00:00:00`), "dd MMM yyyy") : "—"}
                  </td>
                  {canDelete && <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      aria-label={`Delete ${lead.customer_name}`}
                      title="Delete lead"
                      onClick={event => { event.stopPropagation(); onRequestDelete(lead); }}
                      className="inline-flex h-8 w-8 items-center justify-center border border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>}
                </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── LeadsPage ──────────────────────────────────────────────────────────── */
export default function LeadsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useAuth();
  const isRM = authUser?.role === "relationship_manager";
  const isAdmin = ["admin", "superadmin", "manager"].includes(authUser?.role);
  const isSuperAdmin = authUser?.role === "superadmin";
  const currentUserId = authUser?.id || authUser?._id || "";
  const leadView = location.pathname.split("/")[2] || "all";
  const viewTitle = { all: "All Leads", active: "Active Leads", confirmed: "Confirmed Leads", closed: "Closed Leads" }[leadView] || "Leads";
  const availableStatusOptions = VIEW_STATUS_OPTIONS[leadView] || STATUS_FILTER_OPTIONS;

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [shiftingDate, setShiftingDate] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [editFollowup, setEditFollowup] = useState(null);
  const [savingFollowup, setSavingFollowup] = useState(false);
  const [cities, setCities] = useState([]);
  const [relationshipManagers, setRelationshipManagers] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [editLead, setEditLead] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [filterDraft, setFilterDraft] = useState(savedLeadFilters);
  const [activeFilters, setActiveFilters] = useState(savedLeadFilters);
  const [page, setPage] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const downloadQuotation = async lead => {
    try {
      const response = await axios.get(`${API}/leads/${lead.id}/quotation/download`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = lead.quotation_file_name || "quotation";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to download quotation."));
    }
  };
  const [deleteTarget, setDeleteTarget] = useState(null);
  const loadMoreRef = useRef(null);
  const loadingMoreRef = useRef(false);

  // ── Status transition modals ──────────────────────────────────────────
  const [pendingStatusLead, setPendingStatusLead] = useState(null); // full lead object
  const [pendingStatus,     setPendingStatus]     = useState(null);
  const [showContactedDlg,  setShowContactedDlg]  = useState(false);
  const [showClosedWonDlg,  setShowClosedWonDlg]  = useState(false);
  const [showClosedLostDlg, setShowClosedLostDlg] = useState(false);
  const [contactedForm, setContactedForm] = useState({ date: "", action: "", comment: "" });
  const [closedWonForm, setClosedWonForm] = useState({ advance: "", shifting_date: "", special_requests: "" });
  const [lostComment,   setLostComment]   = useState("");
  const [modalSaving,   setModalSaving]   = useState(false);

  /* ── Dispatch status change to correct modal or direct update ── */
  const handleStatusChange = (leadId, newStatus) => {
    const lead = leads.find(l => l.id === leadId);
    setPendingStatusLead(lead);
    setPendingStatus(newStatus);
    if (newStatus === "Contacted") {
      setContactedForm({ date: "", action: "", comment: "" });
      setShowContactedDlg(true);
    } else if (newStatus === "Closed Won") {
      setClosedWonForm({ advance: "", shifting_date: lead?.shifting_date || "", special_requests: "" });
      setShowClosedWonDlg(true);
    } else if (newStatus === "Closed Lost" || newStatus === "Invalid") {
      setLostComment("");
      setShowClosedLostDlg(true);
    } else {
      applyDirectStatusChange(leadId, newStatus);
    }
  };

  /* Direct status patch (Open, Qualified) */
  const applyDirectStatusChange = async (leadId, newStatus) => {
    setStatusUpdating(leadId);
    try {
      const { data } = await axios.patch(`${API}/leads/${leadId}`, { status: newStatus }, { withCredentials: true });
      setLeads(prev => prev.map(l => l.id === leadId ? data : l));
      toast.success(`Status updated to "${newStatus}".`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update status."));
    } finally { setStatusUpdating(null); }
  };

  /* Contacted: patch status + add followup */
  const handleContactedSubmit = async (e) => {
    e.preventDefault();
    if (!contactedForm.date || !contactedForm.action || !contactedForm.comment) {
      toast.error("Please fill in all follow-up fields."); return;
    }
    setModalSaving(true);
    try {
      const { data } = await axios.patch(`${API}/leads/${pendingStatusLead.id}`, { status: "Contacted" }, { withCredentials: true });
      await axios.patch(`${API}/leads/${pendingStatusLead.id}/followup`, {
        next_followup_date:     contactedForm.date,
        next_followup_action:   contactedForm.action,
        next_followup_comments: contactedForm.comment,
      }, { withCredentials: true });
      const { data: fresh } = await axios.get(`${API}/leads/${pendingStatusLead.id}`, { withCredentials: true });
      setLeads(prev => prev.map(l => l.id === pendingStatusLead.id ? fresh : l));
      toast.success("Status set to Contacted.");
      setShowContactedDlg(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed."));
    } finally { setModalSaving(false); }
  };

  /* Closed Won: convert to confirmed lead */
  const handleClosedWonSubmit = async (e) => {
    e.preventDefault();
    if (!closedWonForm.shifting_date) { toast.error("Please enter the shifting date."); return; }
    setModalSaving(true);
    try {
      await axios.post(`${API}/leads/${pendingStatusLead.id}/convert-to-booking`, {
        advance_paid:     parseFloat(closedWonForm.advance || 0),
        shifting_date:    closedWonForm.shifting_date,
        special_requests: closedWonForm.special_requests,
      }, { withCredentials: true });
      const { data: fresh } = await axios.get(`${API}/leads/${pendingStatusLead.id}`, { withCredentials: true });
      setLeads(prev => prev.map(l => l.id === pendingStatusLead.id ? fresh : l));
      toast.success("Lead converted to booking!");
      setShowClosedWonDlg(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to convert."));
    } finally { setModalSaving(false); }
  };

  /* Closed Lost / Invalid: patch status + comment */
  const handleClosedLostSubmit = async (e) => {
    e.preventDefault();
    if (!lostComment.trim()) { toast.error("Please enter a comment."); return; }
    setModalSaving(true);
    try {
      const { data } = await axios.patch(`${API}/leads/${pendingStatusLead.id}`, {
        status:  pendingStatus,
        comment: lostComment.trim(),
      }, { withCredentials: true });
      setLeads(prev => prev.map(l => l.id === pendingStatusLead.id ? data : l));
      toast.success(`Status set to "${pendingStatus}".`);
      setShowClosedLostDlg(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed."));
    } finally { setModalSaving(false); }
  };

  const fetchLeads = async ({ requestedPage = 1, append = false, filters = activeFilters } = {}) => {
    if (append && loadingMoreRef.current) return;
    if (append) { loadingMoreRef.current = true; setLoadingMore(true); }
    try {
      const params = {
        page: requestedPage,
        limit: 50,
        rm: filters.rm,
        status: filters.status,
        added_date: filters.addedDate,
        followup_date: filters.followupDate,
        shifting_date: filters.shiftingDate,
        sort_by: filters.sortBy,
        search: filters.search,
        view: leadView,
      };
      const { data } = await axios.get(`${API}/leads/paginated`, { params, withCredentials: true });
      setLeads(previous => append ? [...previous, ...data.items] : data.items);
      setPage(data.page);
      setTotalLeads(data.total);
      setHasMore(data.has_more);
    } catch {
      toast.error("Failed to fetch leads.");
    } finally {
      setLoading(false);
      if (append) { loadingMoreRef.current = false; setLoadingMore(false); }
    }
  };

  const applyDateSort = sortField => {
    const nextSort = activeFilters.sortBy === sortField ? "created_at" : sortField;
    const nextFilters = { ...activeFilters, sortBy: nextSort };
    setFilterDraft(current => ({ ...current, sortBy: nextSort }));
    setActiveFilters(nextFilters);
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(nextFilters));
    setLoading(true);
    fetchLeads({ requestedPage: 1, filters: nextFilters });
  };

  const handleAssignToMyself = async (leadId) => {
    try {
      const { data } = await axios.patch(`${API}/leads/${leadId}/assign-to-me`, {}, { withCredentials: true });
      setLeads(prev => prev.map(l => l.id === leadId ? data : l));
      toast.success("Lead assigned to you.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to assign lead."));
    }
  };

  const rmNameById = Object.fromEntries(relationshipManagers.map(rm => [String(rm.id), rm.name]));
  const rmIdByName = Object.fromEntries(
    relationshipManagers.map(rm => [rm.name.trim().toLocaleLowerCase(), String(rm.id)])
  );
  const normalizedLeads = leads.map(lead => {
    const assignedRMId = lead.assigned_to ? String(lead.assigned_to) : "";
    const assignedRMName = rmNameById[assignedRMId] || lead.assigned_to_name || "";
    const matchedRMId = assignedRMId || rmIdByName[assignedRMName.trim().toLocaleLowerCase()] || "";
    return {
      ...lead,
      _assignedRMName: assignedRMName,
      _assignedRMKey: matchedRMId
        ? `id:${matchedRMId}`
        : assignedRMName
          ? `name:${assignedRMName.trim().toLocaleLowerCase()}`
          : "unassigned",
      _startingCity: lead.moving_from || "",
      _endingCity: lead.moving_to || "",
    };
  });
  const fallbackRMOptions = normalizedLeads
    .filter(lead => lead._assignedRMKey !== "unassigned")
    .map(lead => ({ key: lead._assignedRMKey, name: lead._assignedRMName || "Unknown RM" }));
  const rmOptions = [
    ...relationshipManagers.map(rm => ({ key: `id:${String(rm.id)}`, name: rm.name })),
    ...fallbackRMOptions,
  ]
    .filter((rm, index, options) => options.findIndex(item => item.key === rm.key) === index)
    .sort((a, b) => a.name.localeCompare(b.name));
  const filteredLeads = normalizedLeads;

  // Split the filtered result for RM role
  const myLeads    = isRM ? filteredLeads.filter(l => l.assigned_to === currentUserId) : filteredLeads;
  const otherLeads = isRM ? filteredLeads.filter(l => l.assigned_to !== currentUserId) : [];

  useEffect(() => {
    const statusIsAvailable = activeFilters.status === "all" || availableStatusOptions.some(option => option.value === activeFilters.status);
    const routeFilters = statusIsAvailable ? activeFilters : { ...activeFilters, status: "all" };
    if (!statusIsAvailable) {
      setFilterDraft(routeFilters);
      setActiveFilters(routeFilters);
      sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(routeFilters));
    }
    fetchLeads({ requestedPage: 1, filters: routeFilters });
    axios.get(`${API}/cities`, { withCredentials: true })
      .then(r => setCities(r.data))
      .catch(() => {});
    axios.get(`${API}/users/relationship-managers`, { withCredentials: true })
      .then(r => setRelationshipManagers(r.data))
      .catch(() => {});
    axios.get(`${API}/lead-sources`, { withCredentials: true })
      .then(r => setLeadSources(r.data))
      .catch(() => {});
  }, [leadView]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore) return undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && !loadingMoreRef.current) {
        fetchLeads({ requestedPage: page + 1, append: true, filters: activeFilters });
      }
    }, { rootMargin: "250px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, page, activeFilters, leadView]);

  const openEditLead = (lead) => {
    setEditLead(lead);
    setForm({
      customer_name: lead.customer_name || "",
      phone: lead.phone || "",
      alternate_number: lead.alternate_number || "",
      lead_source: lead.lead_source || "",
      moving_from: lead.moving_from || "",
      moving_to: lead.moving_to || "",
      from_location: lead.from_location || "",
      to_location: lead.to_location || "",
      load_size: lead.load_size || "",
      quotation: lead.quotation ? String(lead.quotation) : "",
      additional_remarks: lead.additional_remarks || "",
      status: lead.status || "New",
    });
    setShiftingDate(lead.shifting_date ? new Date(lead.shifting_date + "T00:00:00") : null);
    setShowAdd(true);
  };

  const openFollowupEdit = (lead) =>
    setEditFollowup({
      leadId:   lead.id,
      date:     lead.next_followup_date || "",
      action:   lead.next_followup_action || "",
      comments: lead.next_followup_comments || "",
    });

  const closeFollowupEdit = () => setEditFollowup(null);

  const saveFollowup = async (leadId) => {
    setSavingFollowup(true);
    try {
      const { data } = await axios.patch(
        `${API}/leads/${leadId}/followup`,
        {
          next_followup_date:     editFollowup.date,
          next_followup_action:   editFollowup.action,
          next_followup_comments: editFollowup.comments,
        },
        { withCredentials: true }
      );
      setLeads(prev => prev.map(l => l.id === leadId ? data : l));
      toast.success("Follow-up updated.");
      closeFollowupEdit();
    } catch {
      toast.error("Failed to update follow-up.");
    } finally {
      setSavingFollowup(false);
    }
  };

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setShiftingDate(null);
    setCalOpen(false);
    setEditLead(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.phone) {
      toast.error("Customer name and phone are required.");
      return;
    }

    // ── Edit mode ─────────────────────────────────────────────────────────────
    if (editLead) {
      setSaving(true);
      try {
        const { data } = await axios.patch(
          `${API}/leads/${editLead.id}`,
          {
            ...form,
            quotation: parseFloat(form.quotation) || 0,
            shifting_date: shiftingDate ? format(shiftingDate, "yyyy-MM-dd") : (editLead.shifting_date || ""),
          },
          { withCredentials: true }
        );
        setLeads(prev => prev.map(l => l.id === data.id ? data : l));
        toast.success("Lead updated successfully!");
        setShowAdd(false);
        resetForm();
      } catch {
        toast.error("Failed to update lead.");
      } finally {
        setSaving(false);
      }
      return;
    }

    // ── Add mode ──────────────────────────────────────────────────────────────
    setSaving(true);
    try {
      await axios.post(
        `${API}/leads`,
        {
          ...form,
          quotation: parseFloat(form.quotation) || 0,
          shifting_date: shiftingDate ? format(shiftingDate, "yyyy-MM-dd") : "",
        },
        { withCredentials: true }
      );
      toast.success("Lead added successfully!");
      setShowAdd(false);
      resetForm();
      fetchLeads({ requestedPage: 1, filters: activeFilters });
    } catch {
      toast.error("Failed to add lead. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/leads/${deleteTarget.id}`, { withCredentials: true });
      toast.success("Lead deleted.");
      setLeads((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      setTotalLeads(value => Math.max(0, value - 1));
      setDeleteTarget(null);
    } catch {
      toast.error("Failed to delete lead.");
    }
  };

  return (
    <div className="space-y-5" data-testid="leads-page">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A6B]">{viewTitle}</h2>
          <p className="text-xs text-[#475569] mt-0.5">
            {totalLeads} lead{totalLeads !== 1 ? "s" : ""} found · {leads.length} loaded
          </p>
        </div>
        <Button
          data-testid="add-lead-btn"
          onClick={() => { resetForm(); setShowAdd(true); }}
          className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-10 px-5 border-b-2 border-[#FFD700]"
        >
          <Plus size={14} className="mr-1.5" />
          Add Lead
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-4" data-testid="lead-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1.3fr_1fr_1fr_1fr_1fr_1fr_auto] gap-3 items-start">
          <div>
            <label className={lbl}>Customer Name or Number</label>
            <input
              type="search"
              value={filterDraft.search}
              onChange={event => setFilterDraft(filters => ({ ...filters, search: event.target.value }))}
              onKeyDown={event => { if (event.key === "Enter") document.querySelector('[data-testid="apply-lead-filters"]')?.click(); }}
              placeholder="Search customer"
              className="w-full h-10 border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A] focus:border-[#1B2A6B] focus:outline-none"
              data-testid="filter-customer-search"
            />
          </div>
          <div>
            <label className={lbl}>RM</label>
            <select
              value={filterDraft.rm}
              onChange={e => setFilterDraft(f => ({ ...f, rm: e.target.value }))}
              className="w-full h-10 border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A] focus:border-[#1B2A6B] focus:outline-none"
              data-testid="filter-rm"
            >
              <option value="all">All</option>
              <option value="unassigned">Unassigned</option>
              {rmOptions.map(rm => <option key={rm.key} value={rm.key}>{rm.name}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Status</label>
            <select
              value={filterDraft.status}
              onChange={e => setFilterDraft(f => ({ ...f, status: e.target.value }))}
              className="w-full h-10 border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A] focus:border-[#1B2A6B] focus:outline-none"
              data-testid="filter-status"
            >
              <option value="all">All</option>
              {availableStatusOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Added Date</label>
            <input
              type="date"
              value={filterDraft.addedDate}
              onChange={event => setFilterDraft(filters => ({ ...filters, addedDate: event.target.value }))}
              className="w-full h-10 border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A] focus:border-[#1B2A6B] focus:outline-none"
              data-testid="filter-added-date"
            />
          </div>
          <div>
            <label className={lbl}>Follow Up Date</label>
            <input
              type="date"
              value={filterDraft.followupDate}
              onChange={event => setFilterDraft(filters => ({ ...filters, followupDate: event.target.value }))}
              className="w-full h-10 border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A] focus:border-[#1B2A6B] focus:outline-none"
              data-testid="filter-followup-date"
            />
            <button
              type="button"
              role="switch"
              aria-checked={filterDraft.sortBy === "next_followup_date"}
              onClick={() => applyDateSort("next_followup_date")}
              className="mt-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-[#475569]"
              data-testid="sort-by-followup-date"
            >
              Sort By
              <span className={`relative h-4 w-7 rounded-full transition-colors ${filterDraft.sortBy === "next_followup_date" ? "bg-[#1B2A6B]" : "bg-[#CBD5E1]"}`}>
                <span className={`absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white transition-transform ${filterDraft.sortBy === "next_followup_date" ? "translate-x-3" : "translate-x-0"}`} />
              </span>
            </button>
          </div>
          <div>
            <label className={lbl}>Shifting Date</label>
            <input
              type="date"
              value={filterDraft.shiftingDate}
              onChange={event => setFilterDraft(filters => ({ ...filters, shiftingDate: event.target.value }))}
              className="w-full h-10 border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A] focus:border-[#1B2A6B] focus:outline-none"
              data-testid="filter-shifting-date"
            />
            <button
              type="button"
              role="switch"
              aria-checked={filterDraft.sortBy === "shifting_date"}
              onClick={() => applyDateSort("shifting_date")}
              className="mt-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-[#475569]"
              data-testid="sort-by-shifting-date"
            >
              Sort By
              <span className={`relative h-4 w-7 rounded-full transition-colors ${filterDraft.sortBy === "shifting_date" ? "bg-[#1B2A6B]" : "bg-[#CBD5E1]"}`}>
                <span className={`absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white transition-transform ${filterDraft.sortBy === "shifting_date" ? "translate-x-3" : "translate-x-0"}`} />
              </span>
            </button>
          </div>
          <div className="flex gap-2 xl:pt-[25px]">
          <Button
            type="button"
            onClick={() => {
              const nextFilters = { ...filterDraft };
              setActiveFilters(nextFilters);
              sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(nextFilters));
              setLoading(true);
              fetchLeads({ requestedPage: 1, filters: nextFilters });
            }}
            className="h-10 rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold uppercase tracking-widest text-xs px-5 border-b-2 border-[#FFD700]"
            data-testid="apply-lead-filters"
          >
            <Filter size={13} className="mr-1.5" />Filter
          </Button>
          <button
            type="button"
            onClick={() => {
              const cleared = { ...EMPTY_FILTERS };
              setFilterDraft(cleared);
              setActiveFilters(cleared);
              sessionStorage.removeItem(FILTER_STORAGE_KEY);
              setLoading(true);
              fetchLeads({ requestedPage: 1, filters: cleared });
            }}
            className="h-10 border border-[#CBD5E1] bg-white px-4 text-xs font-bold uppercase tracking-widest text-[#475569] hover:border-[#1B2A6B] hover:text-[#1B2A6B]"
            data-testid="clear-lead-filters"
          >
            Clear All Filters
          </button>
          </div>
        </div>
      </div>

      {/* Leads table */}
      <div className="bg-white border border-[#E2E8F0]">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-sm text-[#94A3B8]">
            Loading...
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Users size={40} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">
              {leads.length === 0 ? "No leads yet." : "No leads match the selected filters."}
              {leads.length === 0 && <>{" "}<button onClick={() => { resetForm(); setShowAdd(true); }} className="text-[#1B2A6B] font-semibold underline underline-offset-2">Add your first lead</button></>}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {isRM ? (
              <>
                <LeadsSection
                  title="Your Leads"
                  subtitle={`${myLeads.length} lead${myLeads.length !== 1 ? "s" : ""} assigned to you`}
                  leads={myLeads}
                  navigate={navigate}
                  canDelete={isSuperAdmin}
                  onRequestDelete={setDeleteTarget}
                  onDownloadQuotation={downloadQuotation}
                />
                <LeadsSection
                  title="Other Leads"
                  subtitle={`${otherLeads.length} lead${otherLeads.length !== 1 ? "s" : ""} assigned to others or unassigned`}
                  leads={otherLeads}
                  navigate={navigate}
                  canDelete={isSuperAdmin}
                  onRequestDelete={setDeleteTarget}
                  onDownloadQuotation={downloadQuotation}
                />
              </>
            ) : (
              <LeadsSection
                title={null}
                subtitle={null}
                leads={filteredLeads}
                navigate={navigate}
                canDelete={isSuperAdmin}
                onRequestDelete={setDeleteTarget}
                onDownloadQuotation={downloadQuotation}
              />
            )}
          </div>
        )}
      </div>
      <div ref={loadMoreRef} className="flex min-h-10 items-center justify-center text-xs text-[#64748B]">
        {loadingMore ? "Loading 50 more leads..." : hasMore ? "Scroll to load more" : leads.length > 0 ? "All matching leads loaded" : null}
      </div>

      <Dialog open={Boolean(deleteTarget)} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm rounded-none p-0 gap-0">
          <div className="bg-red-700 px-6 py-4 border-b-4 border-red-900">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Delete Lead</DialogTitle>
          </div>
          <div className="px-6 py-6">
            <p className="text-sm font-medium text-[#0F172A]">Are you sure to Delete the lead?</p>
            {deleteTarget && <p className="mt-1 text-xs text-[#64748B]">{deleteTarget.customer_name} · {deleteTarget.phone}</p>}
          </div>
          <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
            <button type="button" onClick={() => setDeleteTarget(null)} className="h-9 px-5 border border-[#CBD5E1] bg-white text-xs font-bold uppercase tracking-widest text-[#475569]">No</button>
            <Button type="button" onClick={handleDelete} className="h-9 rounded-none bg-red-600 hover:bg-red-700 px-5 text-xs font-bold uppercase tracking-widest text-white">Yes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add / Edit Lead Dialog ── */}
      <Dialog open={showAdd} onOpenChange={(open) => { if (!open) resetForm(); setShowAdd(open); }}>
        <DialogContent className="max-w-2xl rounded-none p-0 gap-0">
          {/* Header */}
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">
              {editLead ? "Edit Lead" : "Add New Lead"}
            </DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">
              {editLead ? `Editing: ${editLead.customer_name}` : "Fill in the customer details below"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
              <FormField label="Customer Name *">
                <Input
                  name="customer_name"
                  value={form.customer_name}
                  onChange={handleChange}
                  placeholder="e.g. Rahul Sharma"
                  data-testid="lead-customer-name"
                  className={INPUT_CLS}
                />
              </FormField>

              <FormField label="Phone *">
                <Input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="e.g. 9876543210"
                  data-testid="lead-phone"
                  className={INPUT_CLS}
                />
              </FormField>

              <FormField label="Alternate Number">
                <Input
                  name="alternate_number"
                  value={form.alternate_number}
                  onChange={handleChange}
                  placeholder="e.g. 9876543211"
                  data-testid="lead-alternate-number"
                  className={INPUT_CLS}
                />
              </FormField>

              <FormField label="Lead Source">
                <Select
                  value={form.lead_source}
                  onValueChange={v => setForm(f => ({ ...f, lead_source: v }))}
                >
                  <SelectTrigger data-testid="lead-source-select" className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm">
                    <SelectValue placeholder={leadSources.length === 0 ? "No sources — add via Master Data" : "Select lead source"} />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {leadSources.map(s => <SelectItem key={s.id} value={s.name} className="rounded-none">{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Moving From">
                <Select
                  value={form.moving_from}
                  onValueChange={v => setForm(f => ({ ...f, moving_from: v }))}
                >
                  <SelectTrigger data-testid="lead-moving-from" className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm">
                    <SelectValue placeholder={cities.length === 0 ? "No cities — add via Master Data" : "Select city"} />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {cities.map(c => <SelectItem key={c.id} value={c.city_name} className="rounded-none">{c.city_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Moving To">
                <Select
                  value={form.moving_to}
                  onValueChange={v => setForm(f => ({ ...f, moving_to: v }))}
                >
                  <SelectTrigger data-testid="lead-moving-to" className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm">
                    <SelectValue placeholder={cities.length === 0 ? "No cities — add via Master Data" : "Select city"} />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {cities.map(c => <SelectItem key={c.id} value={c.city_name} className="rounded-none">{c.city_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="From Location">
                <Input
                  name="from_location"
                  value={form.from_location}
                  onChange={handleChange}
                  placeholder="e.g. Andheri West, Mumbai"
                  data-testid="lead-from-location"
                  className={INPUT_CLS}
                />
              </FormField>

              <FormField label="To Location">
                <Input
                  name="to_location"
                  value={form.to_location}
                  onChange={handleChange}
                  placeholder="e.g. Kothrud, Pune"
                  data-testid="lead-to-location"
                  className={INPUT_CLS}
                />
              </FormField>

              <FormField label="Load Size">
                <Input
                  name="load_size"
                  value={form.load_size}
                  onChange={handleChange}
                  placeholder="e.g. 2 BHK, Office, Warehouse"
                  data-testid="lead-load-size"
                  className={INPUT_CLS}
                />
              </FormField>

              <FormField label="Quotation Given (₹)">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569] text-sm font-medium pointer-events-none">
                    ₹
                  </span>
                  <Input
                    name="quotation"
                    type="number"
                    min="0"
                    value={form.quotation}
                    onChange={handleChange}
                    placeholder="0"
                    data-testid="lead-quotation"
                    className={`${INPUT_CLS} pl-7`}
                  />
                </div>
              </FormField>

              {/* Shifting Date — full width */}
              <div className="sm:col-span-2">
                <FormField label="Shifting Date (Optional)">
                  <Popover open={calOpen} onOpenChange={setCalOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="lead-shifting-date-trigger"
                        className={`w-full flex items-center gap-2.5 px-3 h-11 border text-sm text-left transition-colors focus:outline-none ${
                          shiftingDate
                            ? "border-[#1B2A6B] text-[#0F172A]"
                            : "border-[#E2E8F0] text-[#94A3B8]"
                        }`}
                      >
                        <CalendarIcon size={14} className="text-[#94A3B8] shrink-0" />
                        {shiftingDate
                          ? format(shiftingDate, "dd MMM yyyy")
                          : "Select shifting date"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 rounded-none border-[#E2E8F0]"
                      align="start"
                      style={{ zIndex: 9999 }}
                    >
                      <Calendar
                        mode="single"
                        selected={shiftingDate}
                        onSelect={(d) => {
                          setShiftingDate(d);
                          setCalOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </FormField>
              </div>

              {/* Additional Remarks — full width */}
              <div className="sm:col-span-2">
                <FormField label="Additional Remarks">
                  <textarea
                    name="additional_remarks"
                    value={form.additional_remarks}
                    onChange={handleChange}
                    placeholder="Any special instructions, notes, or additional details..."
                    rows={3}
                    data-testid="lead-additional-remarks"
                    className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 py-2.5 text-sm text-[#0F172A] placeholder:text-[#94A3B8] resize-none"
                  />
                </FormField>
              </div>

              {/* Status — edit mode only */}
              {!!editLead && (
                <div className="sm:col-span-2">
                  <FormField label="Status">
                    <Select
                      value={form.status}
                      onValueChange={v => setForm(f => ({ ...f, status: v }))}
                    >
                      <SelectTrigger className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {["New", "Contacted", "Qualified", "Closed"].map(s => (
                          <SelectItem key={s} value={s} className="rounded-none">{STATUS_DISPLAY[s] || s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                </div>
              )}
            </div>

            {/* Footer */}
            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button
                type="button"
                onClick={() => { resetForm(); setShowAdd(false); }}
                data-testid="lead-cancel-btn"
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] hover:text-[#0F172A] transition-colors"
              >
                Cancel
              </button>
              <Button
                type="submit"
                data-testid="lead-save-btn"
                disabled={saving}
                className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60"
              >
                {saving ? (editLead ? "Updating..." : "Saving...") : (editLead ? "Update Lead" : "Save Lead")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Contacted Modal ── */}
      <Dialog open={showContactedDlg} onOpenChange={o => !o && setShowContactedDlg(false)}>
        <DialogContent className="max-w-sm rounded-none p-0 gap-0">
          <div className="bg-amber-500 px-6 py-4 border-b-4 border-amber-700">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Status: Contacted</DialogTitle>
            <p className="text-white/80 text-xs mt-0.5">Enter follow-up details to proceed</p>
          </div>
          <form onSubmit={handleContactedSubmit} className="px-6 py-5 space-y-4">
            <div>
              <label className={lbl}>Follow-up Date *</label>
              <Input type="date" value={contactedForm.date} onChange={e => setContactedForm(f => ({ ...f, date: e.target.value }))} className={IC} />
            </div>
            <div>
              <label className={lbl}>Action Items *</label>
              <Input placeholder="e.g. Send quotation, Schedule site visit" value={contactedForm.action} onChange={e => setContactedForm(f => ({ ...f, action: e.target.value }))} className={IC} />
            </div>
            <div>
              <label className={lbl}>Comment *</label>
              <textarea rows={3} placeholder="Notes from the interaction…" value={contactedForm.comment} onChange={e => setContactedForm(f => ({ ...f, comment: e.target.value }))} className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 py-2 text-sm resize-none" />
            </div>
            <DialogFooter className="pt-2 gap-2">
              <button type="button" onClick={() => setShowContactedDlg(false)} className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1]">Cancel</button>
              <Button type="submit" disabled={modalSaving} className="rounded-none bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase text-xs h-9 px-5 border-b-2 border-amber-700 disabled:opacity-60">{modalSaving ? "Saving…" : "Save & Update Status"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Closed Won (Convert to Confirmed Lead) Modal ── */}
      <Dialog open={showClosedWonDlg} onOpenChange={o => !o && setShowClosedWonDlg(false)}>
        <DialogContent className="max-w-sm rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Convert to Confirmed Lead</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">Status will be set to Closed Won automatically</p>
          </div>
          <form onSubmit={handleClosedWonSubmit} className="px-6 py-5 space-y-4">
            <div>
              <label className={lbl}>Advance Amount</label>
              <Input type="number" placeholder="0" value={closedWonForm.advance} onChange={e => setClosedWonForm(f => ({ ...f, advance: e.target.value }))} className={IC} />
            </div>
            <div>
              <label className={lbl}>Confirmed Shifting Date *</label>
              <Input type="date" value={closedWonForm.shifting_date} onChange={e => setClosedWonForm(f => ({ ...f, shifting_date: e.target.value }))} className={IC} />
            </div>
            <div>
              <label className={lbl}>Special Requests</label>
              <textarea rows={3} placeholder="Any special instructions or requests…" value={closedWonForm.special_requests} onChange={e => setClosedWonForm(f => ({ ...f, special_requests: e.target.value }))} className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 py-2 text-sm resize-none" />
            </div>
            <DialogFooter className="pt-2 gap-2">
              <button type="button" onClick={() => setShowClosedWonDlg(false)} className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1]">Cancel</button>
              <Button type="submit" disabled={modalSaving} className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold uppercase text-xs h-9 px-5 border-b-2 border-[#FFD700] disabled:opacity-60">{modalSaving ? "Converting…" : "Convert to Confirmed Lead"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Closed Lost / Invalid Modal ── */}
      <Dialog open={showClosedLostDlg} onOpenChange={o => !o && setShowClosedLostDlg(false)}>
        <DialogContent className="max-w-sm rounded-none p-0 gap-0">
          <div className="bg-gray-600 px-6 py-4 border-b-4 border-gray-800">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Status: {pendingStatus}</DialogTitle>
            <p className="text-white/70 text-xs mt-0.5">Please enter a reason or comment</p>
          </div>
          <form onSubmit={handleClosedLostSubmit} className="px-6 py-5 space-y-4">
            <div>
              <label className={lbl}>Comment / Reason *</label>
              <textarea rows={4} placeholder="Why is this lead being marked as lost or invalid?" value={lostComment} onChange={e => setLostComment(e.target.value)} className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 py-2 text-sm resize-none" />
            </div>
            <DialogFooter className="pt-2 gap-2">
              <button type="button" onClick={() => setShowClosedLostDlg(false)} className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1]">Cancel</button>
              <Button type="submit" disabled={modalSaving} className="rounded-none bg-gray-600 hover:bg-gray-700 text-white font-bold uppercase text-xs h-9 px-5 border-b-2 border-gray-800 disabled:opacity-60">{modalSaving ? "Saving…" : "Save & Update Status"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
